import {
  MASCOT_HOLD_MS,
  MASCOT_RANDOM_GAP_MS,
  MASCOT_RANDOM_POOL,
  type MascotFace,
} from '../../../../../shared/ember-mascot.js';

/**
 * The mascot's mood — which face it shows and for how long (拍板 2026-09-10).
 *
 * Two layers. The BASE face is what the page says is going on: `listening` when
 * nothing is, `thinking` while a reply is in flight, `curious` while the person
 * is writing. It stays until the page says otherwise. A NUDGE is a moment laid
 * over the base — glad when the reply lands, delighted when the door lights —
 * held for a few seconds, then the base shows again. And when the base is listening
 * and the page is quiet, the mascot drifts on its own: every 8–20s it picks
 * one of curious / glad / shy for one hold, so it is never a still image, but
 * never busy either. Delighted and thinking are never drawn at random — they mean
 * something happened.
 *
 * A POKE is the person tapping it (拍板 2026-09-10): while the base is listening it
 * shows another face from the poke pool — the drift pool plus delighted, since a
 * tap is a moment the person made — never the one already showing. Off listening
 * (thinking, writing) a tap does nothing: the face is saying something.
 *
 * Pure: timers and randomness are injected so the rule can be tested exactly.
 */

/** What a tap may land on: the drift pool plus delighted. */
export const MASCOT_POKE_POOL: readonly MascotFace[] = [...MASCOT_RANDOM_POOL, 'delighted'];

export interface MascotMoodOptions {
  onFace: (face: MascotFace) => void;
  random?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  /** Let the mascot drift on its own while listening. Default true. */
  drift?: boolean;
}

export interface MascotMood {
  /** The face showing right now. */
  readonly face: MascotFace;
  /** What the page says is going on. `listening` when nothing is. */
  setBase: (face: MascotFace) => void;
  /** A moment over the base; returns to the base after the hold. */
  nudge: (face: MascotFace) => void;
  /** The person tapped it. Only answers while the base is listening; says whether it did. */
  poke: () => boolean;
  /** Stop every timer. The face stays where it is. */
  dispose: () => void;
}

export function createMascotMood(opts: MascotMoodOptions): MascotMood {
  const random = opts.random ?? Math.random;
  const setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const drift = opts.drift !== false;

  let base: MascotFace = 'listening';
  let shown: MascotFace = 'listening';
  let holdTimer: unknown = null;
  let driftTimer: unknown = null;
  let disposed = false;

  const show = (face: MascotFace): void => {
    if (shown === face) return;
    shown = face;
    opts.onFace(face);
  };

  const stopDrift = (): void => {
    if (driftTimer !== null) clearTimer(driftTimer);
    driftTimer = null;
  };

  const scheduleDrift = (): void => {
    stopDrift();
    if (disposed || !drift || base !== 'listening' || holdTimer !== null) return;
    const { min, max } = MASCOT_RANDOM_GAP_MS;
    const gap = min + Math.floor(random() * (max - min));
    driftTimer = setTimer(() => {
      driftTimer = null;
      if (disposed || base !== 'listening' || holdTimer !== null) return;
      const pick = MASCOT_RANDOM_POOL[Math.floor(random() * MASCOT_RANDOM_POOL.length)] ?? 'curious';
      nudge(pick);
    }, gap);
  };

  const endHold = (): void => {
    if (holdTimer !== null) clearTimer(holdTimer);
    holdTimer = null;
  };

  const nudge = (face: MascotFace): void => {
    if (disposed) return;
    endHold();
    stopDrift();
    show(face);
    const hold = face === 'delighted' ? MASCOT_HOLD_MS.delighted : MASCOT_HOLD_MS.default;
    holdTimer = setTimer(() => {
      holdTimer = null;
      show(base);
      scheduleDrift();
    }, hold);
  };

  const setBase = (face: MascotFace): void => {
    if (disposed) return;
    base = face;
    // A change in what is going on outranks a moment in progress: the person
    // sending a message while the mascot is still smiling should see it wait.
    endHold();
    show(base);
    scheduleDrift();
  };

  const poke = (): boolean => {
    if (disposed || base !== 'listening') return false;
    const choices = MASCOT_POKE_POOL.filter((f) => f !== shown);
    const pick = choices[Math.floor(random() * choices.length)] ?? 'curious';
    nudge(pick);
    return true;
  };

  scheduleDrift();

  return {
    get face() { return shown; },
    setBase,
    nudge,
    poke,
    dispose: () => { disposed = true; endHold(); stopDrift(); },
  };
}
